import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { deviceIconMap } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import CountryService from "app/services/CountryService";

import type { LocationInfo } from "./types";

const TooltipContent = ({ locations }: { locations: LocationInfo[] }) => {
    const { translate } = useTranslate();

    return (
        <Stack
            gap="4px"
            marginX="-4"
        >
            {locations.map((location, index) => {
                const country = CountryService.getCountryByCode(location.countryCode);
                const countryId = country?.id;
                const countryName = country?.text ?? location.countryCode;
                const DeviceIcon = deviceIconMap[location.device];

                return (
                    <Stack
                        key={`${location.countryCode}-${location.device}-${location.languageName}`}
                        gap="8px"
                        padding="4px"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                            gap="24px"
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                {countryId ? (
                                    <SWReactCountryIcons
                                        countryCode={countryId}
                                        size="14px"
                                    />
                                ) : null}
                                <Flex flexDirection="column">
                                    <Text
                                        fontVariant="listItem3"
                                        color={colorsPalettes.bluegrey[100]}
                                    >
                                        {`${countryName} - ${location.languageName}`}
                                    </Text>
                                    <Text fontVariant="listItem3">
                                        <Text
                                            as="span"
                                            fontVariant="listItem3"
                                            color={colorsPalettes.carbon[200]}
                                        >
                                            {translate("rankTracker.locationsTooltip.keywords")}
                                        </Text>
                                        <Text
                                            as="span"
                                            fontVariant="listItem3"
                                            color={colorsPalettes.bluegrey[100]}
                                        >
                                            {` ${location.keywordsCount}`}
                                        </Text>
                                    </Text>
                                </Flex>
                            </Flex>
                            <DeviceIcon
                                size="xs"
                                color={colorsPalettes.carbon[250]}
                            />
                        </Flex>
                    </Stack>
                );
            })}
        </Stack>
    );
};

type Props = {
    locations: LocationInfo[];
    children: ReactNode;
};

export const LocationsTooltip = ({ locations, children }: Props) => {
    if (locations.length === 0) {
        return null;
    }

    return (
        <Tooltip
            data-automation="locations-tooltip"
            content={<TooltipContent locations={locations} />}
        >
            {children}
        </Tooltip>
    );
};
